import { useTranslations } from 'next-intl';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto, ShowingGroupedByDateTimeDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { CountryCode } from '@cineamo/api/types/CountryCode';

import {
    ID_CINETECH_AHAUS,
    ID_CINETECH_EMSDETTEN,
    ID_CINETECH_GRONAU,
    ID_CINETECH_RHEINE
} from '@cineamo/api/constants/cinema-ids';
import { Client } from '@cineamo/api/constants/Client.enum';

import { getLocalizedContentDescription } from '@cineamo/api/helpers/content-helper';
import {
    getMovieBackdropBlurDataImage,
    getMovieBackdropImage,
    getMovieOverview,
    getMoviePosterBlurDataImage,
    getMoviePosterImage,
    getMovieReleaseYear,
    getMovieRuntime,
    getMovieTitle,
    MovieImageOptions,
    MovieVersionDto
} from '@cineamo/api/helpers/movie-helper';

import { loadAsset } from './asset-helper';
import { cinemaDayKeyInZone } from './cinema-day';
import { formatDate_yyyyMMdd } from './date-helper';
import {
    convertIso6393ToIso6391,
    getLanguageCodeFromString,
    getLanguageNameFromLocale,
    ISO_639_1_LOCALE,
    ISO_639_3_LOCALE,
    ISO_3166_1_LOCALE,
    TimeZone
} from './locale-helper';
import { isDatetimePast } from './time-helper';

export type ShowingImageFilterOptions = {
    language?: ISO_639_1_LOCALE;
    countryCode?: ISO_3166_1_LOCALE;
    useBackdropAsFallback?: boolean;
    usePosterAsFallback?: boolean;
    usePlaceholder?: boolean;
    movieImageOptions?: MovieImageOptions;
};

const AlternativePosterImage = loadAsset('/images/placeholders/portrait/im-posterFilmPortrait.jpg');
const AlternativeBackdropImage = loadAsset('/images/placeholders/landscape/im-posterFilmLandscape.jpg');

export function getShowingName(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    locale?: string,
    countryCode?: ISO_3166_1_LOCALE
): string {
    // Movie title has priority when a movie exists, then fall back to event-request name,
    // content name and finally the showing's own name (mirrors the cinema-website app).
    // `showing` is optional (grouped list items resolve it lazily), so guarantee a string.
    return (
        getMovieTitle(movie, locale, countryCode) ||
        showing?._embedded?.eventRequest?.name ||
        showing?._embedded?.content?.name ||
        showing?.name ||
        ''
    );
}

export function getShowingDescription(showing?: ShowingDto, movie?: MovieVersionDto | null, locale?: string): string {
    // The content's description is read through the localization helper, not off the raw field:
    // `ContentDto.description` is always the API's default-locale text, and `descriptionEn` is the
    // English one. Reading `.description` directly showed German copy on the English pages.
    return (
        getMovieOverview(movie, locale) ??
        showing?._embedded?.eventRequest?.description ??
        getLocalizedContentDescription(showing?._embedded?.content, locale)
    );
}

/**
 * Artwork for a showing, resolved down one ordered fallback chain: the movie's own image first,
 * then the event request's, then the content's, and last the image uploaded on the showing itself.
 *
 * Every step is resolved with `usePlaceholder: false` so that a step with no image falls through to
 * the next real candidate instead of short-circuiting on a placeholder — the placeholder is applied
 * once, at the end, and only when `usePlaceholder` is left on.
 *
 * `showing`/`movie` are optional: callers read them straight out of a query that may not have
 * resolved yet, and every access below is guarded.
 */
export function getShowingPoster(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
): string | null {
    const {
        language,
        countryCode,
        useBackdropAsFallback = false,
        usePlaceholder = true,
        movieImageOptions
    } = options || {};

    const imageUrl =
        getMoviePosterImage(movie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: false,
            imageOptions: movieImageOptions
        }) ||
        showing?._embedded?.eventRequest?.imageUrl ||
        showing?._embedded?.content?.posterImageUrl ||
        showing?.imageUrl;

    return imageUrl || (usePlaceholder ? AlternativePosterImage : null);
}

/** Blur-up placeholder counterpart of {@link getShowingPoster} — same fallback chain. */
export function getShowingPosterBlurDataImage(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
): string | null {
    const { language, countryCode, useBackdropAsFallback = false, usePlaceholder = true } = options || {};

    const imageUrl =
        getMoviePosterBlurDataImage(movie, {
            language,
            countryCode,
            useBackdropAsFallback,
            usePlaceholder: false
        }) ||
        showing?._embedded?.eventRequest?.imageUrl ||
        showing?._embedded?.content?.posterImageUrl ||
        showing?.imageUrl;

    return imageUrl || (usePlaceholder ? AlternativePosterImage : null);
}

/** Backdrop counterpart of {@link getShowingPoster} — same fallback chain. */
export function getShowingBackdrop(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
): string | null {
    const {
        language,
        countryCode,
        usePosterAsFallback = false,
        usePlaceholder = true,
        movieImageOptions
    } = options || {};

    const imageUrl =
        getMovieBackdropImage(movie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: false,
            imageOptions: movieImageOptions
        }) ||
        showing?._embedded?.eventRequest?.imageUrl ||
        showing?._embedded?.content?.backdropImageUrl ||
        showing?.imageUrl ||
        // `usePosterAsFallback` applies to the content's own artwork too, not just the movie's:
        // a content with only a portrait upload should still fill a backdrop slot.
        (usePosterAsFallback ? showing?._embedded?.content?.posterImageUrl : undefined);

    return imageUrl || (usePlaceholder ? AlternativeBackdropImage : null);
}

/** Blur-up placeholder counterpart of {@link getShowingBackdrop} — same fallback chain. */
export function getShowingBackdropBlurDataImage(
    showing?: ShowingDto,
    movie?: MovieVersionDto | null,
    options?: ShowingImageFilterOptions
): string | null {
    const { language, countryCode, usePosterAsFallback = false, usePlaceholder = true } = options || {};

    const imageUrl =
        getMovieBackdropBlurDataImage(movie, {
            language,
            countryCode,
            usePosterAsFallback,
            usePlaceholder: false
        }) ||
        showing?._embedded?.eventRequest?.imageUrl ||
        showing?._embedded?.content?.backdropImageUrl ||
        showing?.imageUrl ||
        (usePosterAsFallback ? showing?._embedded?.content?.posterImageUrl : undefined);

    return imageUrl || (usePlaceholder ? AlternativeBackdropImage : null);
}

export function getShowingDuration(
    showing: ShowingDto | undefined,
    movie: MovieVersionDto | null | undefined,
    locale: string,
    fallback = 0
): number {
    const showingContent = showing?._embedded?.content;
    const showingEventRequest = showing?._embedded?.eventRequest;

    return showingEventRequest?.duration || showingContent?.duration || getMovieRuntime(movie, locale) || fallback;
}

export function getShowingsProperties(showing: ShowingDto, isMobile?: boolean, includeLanguage?: boolean): string[] {
    const properties: string[] = [];

    if (!showing.isRegularShowtime) {
        properties.push(!isMobile ? 'CINEAMO EVENT' : 'CINEAMO');
    }
    if (showing.isOriginalLanguage && includeLanguage !== false) {
        properties.push('OV');
    }
    if (showing.isThreeDimensional) {
        properties.push('3D');
    } else {
        properties.push('2D');
    }
    if (showing.isDolbyAtmos) {
        properties.push('ATMOS');
    }

    return properties;
}

export function getShowingGroupProperties(showing: ShowingGroupedByDateTimeDto): string[] {
    const properties: string[] = [];

    // if (!showing.isRegularShowtime) {
    //     properties.push(!isMobile ? 'CINEAMO EVENT' : 'CINEAMO');
    // }
    if (showing.isOriginalLanguage) {
        if (showing.isOriginalLanguage) {
            const ovProperty = ['OV', convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE)]
                .filter(Boolean)
                .join(' ');
            properties.push(ovProperty);
        }
    }
    if (showing.isThreeDimensional) {
        properties.push('3D');
    } else {
        properties.push('2D');
    }

    return properties;
}

export function getShowingPropertiesDimension(showing: ShowingDto | ShowingGroupedByDateTimeDto): string | null {
    switch (showing.isThreeDimensional) {
        case true:
            return '3D';
        default:
            return null;
    }
}

/**
 * Returns formatted language info.
 * @example
 * ```ts
 * [
 *  'OmU',
 *  '🔊 EN',
 *  '💬 DE'
 * ]
 * ```
 *
 * @param showing
 * @param locale
 * @param options
 */
export function getShowingPropertiesLanguage(
    showing: ShowingDto | ShowingGroupedByDateTimeDto,
    locale: string,
    options?: { languageFormat?: 'short' | 'long' }
): string[] {
    const language = getShowingLanguages(showing, locale, options);
    const parts: string[] = [];

    if (language.languageInfo) {
        parts.push(language.languageInfo);
    }
    if (language.audioLanguage) {
        parts.push(
            `🔊 ${options?.languageFormat === 'long' ? language.audioLanguage : language.audioLanguage.toUpperCase()}`
        );
    }
    if (language.subtitleLanguage) {
        parts.push(
            `💬 ${options?.languageFormat === 'long' ? language.subtitleLanguage : language.subtitleLanguage.toUpperCase()}`
        );
    }

    return parts;
}

/**
 * Returns formatted language info.
 * @example
 * ```ts
 * {
 *  languageInfo: 'OmU',
 *  audioLanguage: 'EN',
 *  subtitledLanguage: 'DE'
 * }
 * ```
 *
 * or with options.languageFormat === 'long'
 * ```ts
 * {
 *  languageInfo: 'OmU',
 *  audioLanguage: 'English',
 *  subtitledLanguage: 'German'
 * }
 * ```
 *
 * @param showing
 * @param locale
 * @param options
 *
 * @deprecated Use {@link getShowingTicketLanguages} for the program/ticket UI — it is a 1:1 port of
 * the mobile app's `getLanguages` (matching OmU/OV/subtitle semantics). This variant additionally
 * emits a 'UT'/'Sub' info label for subtitled-but-not-original showings and compares the locale
 * against the ISO-639-1 language, which diverges from the mobile behavior.
 */
export function getShowingLanguages(
    showing: ShowingDto | ShowingGroupedByDateTimeDto,
    locale: string,
    options?: {
        languageFormat?: 'short' | 'long';
        languages?: Record<ISO_639_3_LOCALE, string>;
    }
): { audioLanguage?: string; subtitleLanguage?: string; languageInfo?: string } {
    const language: { audioLanguage?: string; subtitleLanguage?: string; languageInfo?: string } = {};

    const longLanguage =
        options?.languages?.[showing.language as ISO_639_3_LOCALE] ??
        getLanguageNameFromLocale(showing.language ?? '', locale);
    const longSubtitleLanguage =
        options?.languages?.[showing.subtitledLanguage as ISO_639_3_LOCALE] ??
        getLanguageNameFromLocale(showing.subtitledLanguage ?? '', locale);

    const iso6391Language = convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE);
    const iso6391SubtitleLanguage = convertIso6393ToIso6391(showing.subtitledLanguage as ISO_639_3_LOCALE);

    const finalLanguage = options?.languageFormat === 'long' ? longLanguage : iso6391Language;
    const finalSubtitleLanguage = options?.languageFormat === 'long' ? longSubtitleLanguage : iso6391SubtitleLanguage;

    // If isOriginalLanguage && isSubtitled it means the showing is in original language + subtitle (OmU)
    if (showing.isOriginalLanguage === true && showing.isSubtitled === true) {
        language.languageInfo = 'OmU';
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = finalSubtitleLanguage;
        }
    }
    // If isSubtitled but not in original language: a dubbed/non-original showing that still has subtitles.
    // Labelled UT (Untertitel) in German / Sub in English — distinct from OmU, which requires the original language.
    else if (showing.isSubtitled === true) {
        language.languageInfo = getLanguageCodeFromString(locale) === 'en' ? 'Sub' : 'UT';
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = finalSubtitleLanguage;
        }
    }
    // If isOriginalLanguage && originalLanguage it means the showing is in original language (OV)
    else if (showing.isOriginalLanguage === true && locale !== iso6391Language) {
        language.languageInfo = 'OV';
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
    }

    // Fallback if neither OmU nor OV, then just add the language like EN (if available)
    if (Object.keys(language).length === 0) {
        if (showing.language) {
            language.audioLanguage = finalLanguage;
        }
    }

    return language;
}

export type ShowingTicketLanguages = {
    audioLanguage: string | null;
    subtitleLanguage: string | null;
    languageInfo: string | null;
};

/**
 * Language info for the program ticket buttons — 1:1 port of the mobile app's
 * `getLanguages` (`packages/utils/src/showings-info-helper.ts`, SHORT format):
 * OmU (original + subtitled) → subtitled (audio/subtitle only, no info label) →
 * OV (original, locale differs) → fallback plain audio language. Short codes are
 * returned uppercase (e.g. 'EN'), exactly like the mobile helper.
 */
export function getShowingTicketLanguages(
    showing: ShowingDto | ShowingGroupedByDateTimeDto,
    locale: string | undefined
): ShowingTicketLanguages {
    const language: ShowingTicketLanguages = {
        audioLanguage: null,
        subtitleLanguage: null,
        languageInfo: null
    };

    function formatLanguage(languageCode: string): string {
        return convertIso6393ToIso6391(languageCode as ISO_639_3_LOCALE)?.toUpperCase() ?? '';
    }

    // Case 1: Original language with subtitles (OmU)
    if (showing.isOriginalLanguage && showing.isSubtitled) {
        language.languageInfo = 'OmU';
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = formatLanguage(showing.subtitledLanguage);
        }
    }
    // Case 2: Subtitled but not original language (no info label, mirrors mobile)
    else if (showing.isSubtitled) {
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
        if (showing.subtitledLanguage) {
            language.subtitleLanguage = formatLanguage(showing.subtitledLanguage);
        }
    }
    // Case 3: Original language but locale differs (OV) — comparison kept verbatim from mobile
    else if (showing.isOriginalLanguage && locale !== showing.language) {
        language.languageInfo = 'OV';
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
    }

    // Fallback: just the audio language if nothing else matched
    if (!language.audioLanguage && !language.subtitleLanguage && !language.languageInfo) {
        if (showing.language) {
            language.audioLanguage = formatLanguage(showing.language);
        }
    }

    return language;
}

/**
 * @param duration - Duration in minutes
 * @param t - Translations
 * @returns {string} duration in format `DDh DDm` localized
 */
export function formatDuration(duration: number, t: ReturnType<typeof useTranslations>): string {
    const hours = Math.floor(duration / 60);
    const hoursStr = hours > 0 ? t('common.time.hour-short', { hours }) : '';
    const minutes = duration % 60;
    const minutesStr = minutes > 0 ? t('common.time.minute-short', { minutes }) : '';

    return [hoursStr, minutesStr].filter(Boolean).join(' ');
}

export function getShowingInfo(options: {
    showing?: ShowingDto;
    content?: ContentDto;
    movie?: MovieVersionDto | null;
    countryCode?: CountryCode;
    locale: string;
    t: ReturnType<typeof useTranslations>;
    showMovieDuration?: boolean;
    showGenres?: boolean;
    ageRatingText?: string;
}): Array<string> {
    const countryCode =
        options.countryCode ||
        options.content?._embedded?.cinema?.countryCode ||
        options.showing?._embedded?.cinema?.countryCode;
    const movie = options.movie;
    const movieReleaseYear = getMovieReleaseYear(movie, countryCode);

    const runtimeMinutes =
        (options.showMovieDuration && getMovieRuntime(movie, options.locale)) ||
        getShowingDuration(options.showing, movie as MovieVersionDto, options.locale) ||
        options.content?.duration ||
        options.showing?._embedded?.content?.duration;
    const runtimeFormatted = runtimeMinutes ? formatDuration(runtimeMinutes, options.t) : undefined;
    const genres = options.showGenres ? movie?.genres : [];

    return [
        movieReleaseYear,
        runtimeFormatted,
        (genres?.slice(0, 3) || []).map((genre) => options.t(`genres.tmdb-genre-${genre.id}`)).join(', '),
        options.ageRatingText
    ].filter((it): it is string => !!it);
}

export function getGroupedShowings(showings: ShowingDto[], client: Client): ShowingGroupedByDateTimeDto[] {
    const groupedShowings: ShowingGroupedByDateTimeDto[] = [];
    showings.forEach((showing) => {
        const showingDate = showing.startDatetime;
        const showingGroup = groupedShowings?.find((group) => {
            return (
                group.startDatetime === showing.startDatetime &&
                group.isOriginalLanguage === showing.isOriginalLanguage &&
                group.language === showing.language &&
                group.isThreeDimensional === showing.isThreeDimensional &&
                group.cinemaRoomId === showing.cinemaRoomId
            );
        });
        if (showingGroup) {
            showingGroup.showings.push(showing);
        } else {
            groupedShowings?.push({
                startDatetime: showingDate,
                cinemaRoomId: showing.cinemaRoomId,
                isThreeDimensional: showing.isThreeDimensional,
                language: showing.language,
                isOriginalLanguage: showing.isOriginalLanguage,
                originalLanguage: showing.originalLanguage,
                isDubbed: showing.isDubbed,
                dubbedLanguage: showing.dubbedLanguage,
                isSubtitled: showing.isSubtitled,
                subtitledLanguage: showing.subtitledLanguage,
                onlineTicketUrl: getOnlineTicketUrl(showing, client) ?? undefined,
                showings: [showing]
            });
        }
    });
    return groupedShowings;
}

export function getShowingTechnologies(showing: ShowingDto) {
    const technologies = {
        isImax: 'imax',
        isDolbyAtmos: 'dolby-atmos',
        isDolbyVision: 'dolby-vision',
        isDolbyCinema: 'dolby-cinema',
        is4DX: '4-dx',
        isScreenX: 'screen-x',
        isHFR: 'hfr',
        isLive: 'live',
        isDbox: 'd-box',
        isInfinityVision: 'infinity-vision'
    };

    return Object.entries(technologies)
        .filter(([key]) => showing[key as keyof ShowingDto])
        .map(([, value]) => value);
}

/**
 * Depending on the userLocale this fn will return true or false.
 * E.g. the showing `originalLanguage` is `en` and the userLocale is `en`, this fn will return `false`.
 * If the showing `originalLanguage` is `en` and the userLocale is `de`, this fn will return `true` because this
 * is what the user would expect to see.
 *
 * @param showing
 * @param cinema
 */
export function isOVShowing(showing: ShowingDto, cinema: CinemaDto): boolean {
    const locale = cinema?.countryCode
        ? // "und" = unknown/undefined language
          new Intl.Locale(`und-${cinema.countryCode}`)?.maximize()?.language
        : null;
    return !!showing.isOriginalLanguage && locale !== convertIso6393ToIso6391(showing.language as ISO_639_3_LOCALE);
}

export function getOnlineTicketUrl(showing: ShowingDto, client: Client, locale?: string): string | null {
    let ticketUrl: string | null | undefined = null;

    switch (client) {
        case Client.Web:
            ticketUrl = showing?.ticketUrls?.web;
            break;
        case Client.iOS:
            ticketUrl = showing?.ticketUrls?.ios;
            break;
        case Client.Android:
            ticketUrl = showing?.ticketUrls?.android;
            break;
        case Client.CinemaWebsite:
            ticketUrl = showing?.ticketUrls?.cinemaWebsite;
            break;
    }

    if (!ticketUrl) {
        ticketUrl = showing?.ticketUrls?.default ?? showing?.onlineTicketUrl;
    }

    const isCineamoOnlineTicketingEnabled =
        process.env.NEXT_PUBLIC_ENV_CIN_DOMAIN && ticketUrl?.includes(process.env.NEXT_PUBLIC_ENV_CIN_DOMAIN);

    if (isCineamoOnlineTicketingEnabled) {
        ticketUrl = `/booking/${showing.id}`;
    }

    if (isTicketInternationalUrl(showing)) {
        ticketUrl = `/booking/embedded/${showing.id}`;
    }

    // Prepend the locale so internal links preserve the user's current language instead of
    // falling back to the middleware's Accept-Language / cookie detection (CIN-15166).
    if (locale && ticketUrl?.startsWith('/')) {
        ticketUrl = `/${locale}${ticketUrl}`;
    }

    return ticketUrl ?? null;
}

/**
 * If the showing is a Ticket International cinema, open the booking in an iframe.
 * @param showing
 */
function isTicketInternationalUrl(showing: ShowingDto) {
    switch (showing.cinemaId) {
        case ID_CINETECH_EMSDETTEN:
        case ID_CINETECH_AHAUS:
        case ID_CINETECH_GRONAU:
        case ID_CINETECH_RHEINE:
            return true;
        default:
            return false;
    }
}

export function getHoursUntilShowing(startDatetime: ShowingDto['startDatetime']): number | undefined {
    if (!startDatetime) {
        return undefined;
    }
    return Math.round((new Date(startDatetime).getTime() - new Date().getTime()) / 1000 / 60 / 60);
}

/**
 * Whether online ticket sale has not opened yet for a showing.
 *
 * The ticket system (e.g. Cinetixx) can define a sale-start (`startSaleDatetime`)
 * before which tickets must not be bookable. When this returns true the buy
 * button must be disabled and the user told when sale opens.
 */
export function isShowingSalePending(showing: ShowingDto): boolean {
    if (!showing.startSaleDatetime) {
        return false;
    }
    return new Date(showing.startSaleDatetime).getTime() > Date.now();
}

export type ShowingGroupedByDay = {
    date: string;
    showings: ShowingGroupedByDateTimeDto[];
};

/** Buckets already-grouped showings by calendar day, keyed by the raw `yyyy-MM-dd` of their start. */
/**
 * @param timeZone Groups by Cinema Day on that zone's clock — a 03:47 screening then belongs to the
 *        evening it was programmed under rather than the calendar day it spills into. Omit to keep
 *        grouping by raw calendar date in the local zone.
 */
export function getShowingsGroupedByDay(
    groupedShowings: ShowingGroupedByDateTimeDto[],
    timeZone?: TimeZone
): ShowingGroupedByDay[] {
    const showingsGroupedByDay = groupedShowings.reduce<Record<string, ShowingGroupedByDateTimeDto[]>>(
        (result, groupedShowing) => {
            const date = timeZone
                ? cinemaDayKeyInZone(groupedShowing.startDatetime ?? '', timeZone)
                : formatDate_yyyyMMdd(groupedShowing.startDatetime ?? '');
            const existing = result[date];
            if (existing) {
                existing.push(groupedShowing);
            } else {
                result[date] = [groupedShowing];
            }
            return result;
        },
        {}
    );

    return Object.entries(showingsGroupedByDay).map(([date, showings]) => ({ date, showings }));
}

function resolveShowingOfferAvailability(showing: ShowingDto): string {
    if (isDatetimePast(showing.startDatetime, true)) {
        return 'https://schema.org/SoldOut';
    }
    if (isShowingSalePending(showing)) {
        return 'https://schema.org/PreOrder';
    }
    return 'https://schema.org/InStock';
}

/**
 * schema.org `ScreeningEvent.offers` for a showing, or `undefined` when there is nothing to offer
 * (no ticket URL at all) — callers should omit the `offers` property in that case rather than
 * invent an availability.
 */
export function resolveShowingTicketOffer(
    showing: ShowingDto,
    baseUrl: string,
    client: Client
): { url: string; availability: string } | undefined {
    const ticketUrl = getOnlineTicketUrl(showing, client);
    if (!ticketUrl) {
        return undefined;
    }

    return {
        url: ticketUrl.startsWith('http') ? ticketUrl : `${baseUrl}${ticketUrl}`,
        availability: resolveShowingOfferAvailability(showing)
    };
}
